// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/useSendWhenIdle.test.tsx
import { useCallback, useEffect, useRef, useState } from "react";

import { type AskV2CommentContext } from "./types";

/** A message the chat refused while a turn ran, held until the turn ends. */
export type HeldSend = { message: string; commentContext: AskV2CommentContext };

/**
 * Sends of one held request before it is dropped.
 *
 * why a cap: `send` also returns false for a post that failed, and a failed
 * turn reads as idle, so without one a held request retried as fast as the
 * server answered, a failed bubble each time (bobai on #6922). The last failed
 * bubble stays in the thread, so she sees it did not go.
 */
export const MAX_HELD_SEND_ATTEMPTS = 3;

type UseSendWhenIdleArgs = {
  inFlight: boolean;
  send: (opts: { message: string }) => Promise<boolean>;
  setCommentContext: (context: AskV2CommentContext | null) => void;
  removeCommentContext: (id: string) => void;
  /** The chip on the composer now. */
  currentCommentContext: () => AskV2CommentContext | null;
};

/**
 * Holds messages the chat refused while a turn ran and sends each, with its
 * chip, once the page's turn is over. It lives with the page's transport, not
 * with the pane that asked, so a rule request a reviewer made mid-review still
 * reaches the chat after she closes the review or switches its version
 * (warhol spec surbhi.kapoor/0100).
 */
export function useSendWhenIdle({
  inFlight,
  send,
  setCommentContext,
  removeCommentContext,
  currentCommentContext,
}: UseSendWhenIdleArgs) {
  const heldRef = useRef<HeldSend[]>([]);
  const attemptsRef = useRef(new Map<HeldSend, number>());
  const sendingRef = useRef(false);
  const [held, setHeld] = useState(0);

  const sendWhenIdle = useCallback((request: HeldSend) => {
    heldRef.current = [...heldRef.current, request];
    setHeld((count) => count + 1);
  }, []);

  useEffect(() => {
    const next = heldRef.current[0];
    if (!next || inFlight || sendingRef.current) return;
    // why the ref: the effect can run again before `send` marks the turn in
    // flight, and a second pass would post the same request twice
    sendingRef.current = true;
    // why keep hers: setCommentContext replaces the composer's chip, and a
    // chip she attached since the request was held is hers to send, not lost
    // to it, whether the send goes or is refused (bobai on #6922)
    const hers = currentCommentContext();
    setCommentContext(next.commentContext);
    // why the catch: a rejected send skipped the bookkeeping below, so the
    // hold stayed "sending" and nothing held was ever posted again
    void send({ message: next.message })
      .catch(() => false)
      .then((sent) => {
        sendingRef.current = false;
        const attempts = (attemptsRef.current.get(next) ?? 0) + 1;
        attemptsRef.current.set(next, attempts);
        if (!sent) {
          // why: a chip left on a refused send rides on her next message
          removeCommentContext(next.commentContext.id);
        }
        if (sent || attempts >= MAX_HELD_SEND_ATTEMPTS) {
          heldRef.current = heldRef.current.filter((one) => one !== next);
          attemptsRef.current.delete(next);
          setHeld((count) => count - 1);
        }
        // why only an empty composer: a chip she attached while the send ran is
        // newer than the one she had
        if (
          hers &&
          hers.id !== next.commentContext.id &&
          !currentCommentContext()
        ) {
          setCommentContext(hers);
        }
      });
  }, [
    currentCommentContext,
    held,
    inFlight,
    removeCommentContext,
    send,
    setCommentContext,
  ]);

  return sendWhenIdle;
}
